import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Card, Box, Grid, CardMedia, CardContent, IconButton, Typography, CardActions,
  SpeedDial,
} from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import AddIcon from '@mui/icons-material/Add';
import ScrollDialog from 'components/ScrollDialog';
import ImageResize from './ImageResize';
import { useSnackbar } from 'notistack';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { saveLibraryCoverPhoto, uploadLibraryPhoto, deleteLibraryPhoto } from 'services/property';
import useConfirm from 'core/hooks/useConfirm';

// 
const UploadPropertyFiles = ({libraryId, libraryPhotos = []}) => {
  const [upImg, setUpImg] = useState();
  const fileInputRef = useRef(null);
  const [openModal, setOpenModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [coverImage, setCoverImage] = useState(`/rest/properties/images/${libraryId}/coverPhoto.jpg`);
  const [imageType, setImageType] = useState(null);
  const [otherImages, setOtherImages] = useState([]);
  const confirm = useConfirm();

  useEffect(() => {
    setOtherImages([...libraryPhotos.map((ele) => ({src: buildOtherPhotoUrl(ele), name: ele}))])
    return () => {};
  }, [libraryPhotos]);

  const buildOtherPhotoUrl = (fileName) => {
    return `/rest/properties/images/${libraryId}/${fileName}`;
  }
  

  const onModalClose = useCallback(
    () => {
      setOpenModal(false);
      clearImageSelection();
    },
    [],
  );

  const ShowModal = () => {
    // event.preventDefault();
    setOpenModal(true);
  }

  const closeModal = () => {
    setOpenModal(false);
    clearImageSelection();
  }

  const clearImageSelection = () => {
    setUpImg(null);
    fileInputRef.current.value="";
  }

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        //Initiate the JavaScript Image object.
        const image = new Image();

        //Set the Base64 string return from FileReader as source.
        image.src = reader.result;

        //Validate the File Height and Width.
        image.onload = (e) => {
          console.log('e', e)
          var height = image.height;
          var width = image.width;
          if (height < 768 || width < 1366) {
            enqueueSnackbar("Image should be atleast 768px height and 1366px width", {variant: 'error'});
            return false;
          }
          setUpImg(reader.result);
          ShowModal();
          return true;
        };
        
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const selectCoverPhoto = () => {
    setImageType('cover');
    selectImage();
  }

  const selectOtherPhotos = () => {
    setImageType('other');
    selectImage();
  }
  
  const selectImage = () => {
    fileInputRef.current.click();
  }

  function dataURLtoFile(dataurl, filename) {
    var arr = dataurl.split(','),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]), 
        n = bstr.length, 
        u8arr = new Uint8Array(n);
    while(n--){
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, {type:mime});
  }

  const uploadCoverPhoto = (imgSrc) => {
    saveLibraryCoverPhoto({ 
      image: dataURLtoFile(imgSrc, 'cover-image.jpg'), 
      libraryId
    }).then(({response, error}) => {
      if(response){
        setCoverImage(imgSrc);
        enqueueSnackbar("Image uploaded successfully", {variant: 'success'});
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
        clearImageSelection()
      }
    });
  }

  const uploadOtherImages = (imgSrc) => {
    uploadLibraryPhoto({ 
      image: dataURLtoFile(imgSrc, 'other-image.jpg'), 
      libraryId 
    }).then(({response, error}) => {
      if(response){
        setOtherImages([...otherImages, { src: buildOtherPhotoUrl(response.fileName), name: response.fileName }])
        enqueueSnackbar("Image uploaded successfully", {variant: 'success'});
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
        clearImageSelection()
      }
    });
  }

  const uploadFile = useCallback((img) => {
    const imgSrc = img.src;
    if(imageType === 'cover'){
      uploadCoverPhoto(imgSrc);
    } else {
      uploadOtherImages(imgSrc);
    }
    closeModal();
    setImageType(null);
  }, [imageType, otherImages]);

  const onPhotoDelete = (imageIndex) => {
    confirm({ title: 'Delete Photo?', description: 'Are you sure you want to delete this photo?' })
    .then(() => { 
      deletePhoto(imageIndex);
    })
  }

  const deletePhoto = (imageIndex) => {
    const temp = [...otherImages];
    const [deletedItem] = temp.splice(imageIndex, 1);

    deleteLibraryPhoto({ 
      fileName: deletedItem.name, 
      libraryId 
    }).then(({response, error}) => {
      if(response){
        setOtherImages([...temp]);
        enqueueSnackbar("Image deleted successfully", {variant: 'success'});
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });

  }

  return (
    <Box sx={{ mb: 2, mt:2, px: 4, }} >
      <Grid container columnSpacing={2} >
        <Grid item xs={12} sm={12} md={12} lg={12} sx={{mb: 4, }}>
          <Card sx={{ maxWidth: '70%', position: 'relative',  margin: '0 auto'}}>
            <CardMedia
              component="img"
              height="auto"
              image={coverImage}
              alt="green iguana"
            />
            <CardContent >
              <Typography  variant="p" component="div">
                Cover Photo
              </Typography>
            </CardContent>
            <CardActions sx={{ position: 'absolute', bottom: 0, right: 0}}>
              <IconButton sx={{borderRadius: '50%', background: '#f3f3f3'}} onClick={selectCoverPhoto}>
                <CreateIcon />
              </IconButton>
            </CardActions>
          </Card>
        </Grid>
        {otherImages.map((ele, i) => (
          <Grid item xs={12} sm={12} md={6} lg={4} sx={{mb: 3}} key={i}>
            <Card sx={{  position: 'relative' }}>
            <CardMedia
              component="img"
              height="196"
              image={ele.src}
            />
            {/* <CardContent >
              <Typography  variant="p" component="div">
                Cover Photo
              </Typography>
            </CardContent> */}
            <CardActions sx={{ position: 'absolute', bottom: 0, right: 0}}>
              <IconButton variant="raised" sx={{
                borderRadius: '50%', background: '#f3f3f3', color: '#a51414',
                "&.MuiButtonBase-root:hover": {
                  bgcolor: "#f3f3f3"
                }
                }}  onClick={() => onPhotoDelete(i)}
              >
                <DeleteForeverIcon />
              </IconButton>
            </CardActions>
          </Card>
          </Grid>
        ))}
      </Grid>
      <SpeedDial
        onClick={selectOtherPhotos}
        ariaLabel="Add Image"
        sx={{ position: 'absolute', bottom: 75, right: 75 }}
        icon={<AddIcon />}
      />
      <input type="file" accept="image/*" ref={fileInputRef} onChange={onSelectFile} style={{display: 'none'}} />
      <ScrollDialog maxWidth="xl" onModalClose={onModalClose} open={openModal} title="Image Upload">
        <ImageResize upImg={upImg} uploadFile={uploadFile} />
      </ScrollDialog>
    </Box>
  );
}
 
export default React.memo(UploadPropertyFiles);
